/** Reset across sessions; stale network responses cannot restore old counts. */
export function createNotificationSummaryStore(load: () => Promise<number | undefined>) {
  let count: number | undefined;
  let generation = 0;
  const listeners = new Set<() => void>();
  const emit = () => listeners.forEach((listener) => listener());
  return {
    getSnapshot: () => count,
    subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; },
    reset() { generation++; count = undefined; emit(); },
    async refresh() {
      const current = ++generation;
      try { const result = await load(); if (current === generation) { count = result; emit(); } }
      catch { if (current === generation) { count = undefined; emit(); } }
    },
  };
}

interface SummaryRefreshEnvironment {
  windowEvents: Pick<EventTarget, "addEventListener" | "removeEventListener">;
  documentEvents: Pick<EventTarget, "addEventListener" | "removeEventListener">;
  isVisible: () => boolean;
  isAuthorized: () => boolean;
  refresh: () => Promise<void>;
  reset: () => void;
  setInterval: (callback: () => void, milliseconds: number) => number;
  clearInterval: (id: number) => void;
}

/** Visible-page polling plus return-to-page refresh, with complete teardown. */
export function startNotificationSummaryRefresh(env: SummaryRefreshEnvironment) {
  let disposed = false;
  let expired = false;
  let running = false;
  let queued = false;
  const refresh = () => {
    if (disposed || expired || !env.isVisible() || !env.isAuthorized()) return;
    if (running) { queued = true; return; }
    running = true;
    void env.refresh().catch(() => { /* Errors remain in the summary store. */ }).finally(() => {
      running = false;
      if (queued) { queued = false; refresh(); }
    });
  };
  const unauthorized = () => { expired = true; queued = false; env.reset(); };
  env.windowEvents.addEventListener("focus", refresh);
  env.windowEvents.addEventListener("agent:notifications-changed", refresh);
  env.windowEvents.addEventListener("agent:unauthorized", unauthorized);
  env.documentEvents.addEventListener("visibilitychange", refresh);
  const timer = env.setInterval(refresh, 30_000);
  refresh();
  return () => {
    disposed = true;
    queued = false;
    env.clearInterval(timer);
    env.windowEvents.removeEventListener("focus", refresh);
    env.windowEvents.removeEventListener("agent:notifications-changed", refresh);
    env.windowEvents.removeEventListener("agent:unauthorized", unauthorized);
    env.documentEvents.removeEventListener("visibilitychange", refresh);
    env.reset();
  };
}
